"use client";

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createItemAPI, updateItemAPI } from "../api/itemsApi";
import { parseCsv } from "@/utils/csvParse";
import { parseXlsxFirstSheet } from "@/utils/xlsxParse";
import { parseImportTemporal } from "@/utils/importTemporal";
import {
  buildRelationLookup,
  fetchAllRelatedItems,
  isRelationNoMatchError,
  resolveMultiRelationValueForImport,
  resolveRelationValueForImport,
  type RelationLookup,
} from "./relationImportLookup";
import { Loader2, AlertCircle, CheckCircle2, Upload } from "lucide-react";
import { CsvImportRelationFixDialog } from "./CsvImportRelationFixDialog";

const PREVIEW_ROWS = 5;
const EMPTY = "__skip__";
/** Synthetic mapping target: CSV `uid` column for update-by-item-id (not a collection field). */
export const IMPORT_ITEM_UID_FIELD = "__lwItemUid";

/** When a row has a uid but that item does not exist here (e.g. export from another env), create instead of failing. */
async function importRowCreateOrUpdate(
  slug: string,
  itemUid: string | null,
  payload: Record<string, unknown>,
  organization: string | null | undefined
): Promise<void> {
  if (itemUid) {
    try {
      await updateItemAPI(slug, itemUid, payload, organization);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("HTTP 404")) {
        await createItemAPI(
          slug,
          { ...payload, uid: itemUid },
          organization
        );
        return;
      }
      throw err;
    }
  } else {
    await createItemAPI(slug, payload, organization);
  }
}

/** Radix Select forbids SelectItem value=""; CSV can have empty header cells while editing. */
const COLUMN_BY_INDEX_PREFIX = "__lw_csv_col_idx__:";

function columnSelectValue(index: number, header: string): string {
  return header === "" ? `${COLUMN_BY_INDEX_PREFIX}${index}` : header;
}

function columnIndexFromMappedValue(mapped: string, headers: string[]): number {
  if (mapped.startsWith(COLUMN_BY_INDEX_PREFIX)) {
    const n = Number(mapped.slice(COLUMN_BY_INDEX_PREFIX.length));
    if (!Number.isInteger(n) || n < 0 || n >= headers.length) return -1;
    return n;
  }
  const i = headers.indexOf(mapped);
  return i;
}

function rawCsvCellForMappedField(
  rowIndex: number,
  fieldName: string,
  columnMapping: Record<string, string>,
  headers: string[],
  rows: string[][]
): string {
  const csvCol = columnMapping[fieldName];
  if (csvCol === undefined) return "";
  const colIndex = columnIndexFromMappedValue(csvCol, headers);
  const row = rows[rowIndex];
  if (colIndex < 0 || !row || colIndex >= row.length) return "";
  return String(row[colIndex] ?? "");
}

function normalizeHeaderToken(s: string): string {
  return s.toLowerCase().replace(/[\s_\-./]+/g, "");
}

function cellToString(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function jsonArrayOfObjectsToSheet(arr: Record<string, unknown>[]): { headers: string[]; rows: string[][] } {
  const headerOrder: string[] = [];
  const seen = new Set<string>();
  for (const obj of arr) {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) continue;
    for (const k of Object.keys(obj)) {
      if (!seen.has(k)) {
        seen.add(k);
        headerOrder.push(k);
      }
    }
  }
  const headers = headerOrder;
  const rows = arr.map((obj) => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
      return headers.map(() => "");
    }
    return headers.map((h) => cellToString(obj[h]));
  });
  return { headers, rows };
}

function jsonArrayOfArraysToSheet(arr: unknown[][]): { headers: string[]; rows: string[][] } {
  if (arr.length === 0) return { headers: [], rows: [] };
  const first = arr[0];
  const headers = first.map((c) => cellToString(c));
  const rows = arr.slice(1).map((row) => headers.map((_, i) => cellToString(row[i])));
  return { headers, rows };
}

/** Top-level JSON array: either an array of objects (records) or an array of row arrays (header row first). */
function parseJsonToSheet(parsed: unknown): { headers: string[]; rows: string[][] } | null {
  if (!Array.isArray(parsed)) return null;
  if (parsed.length === 0) return { headers: [], rows: [] };
  const first = parsed[0];
  if (first !== null && typeof first === "object" && !Array.isArray(first)) {
    return jsonArrayOfObjectsToSheet(parsed as Record<string, unknown>[]);
  }
  if (Array.isArray(first)) {
    return jsonArrayOfArraysToSheet(parsed as unknown[][]);
  }
  return null;
}

function parseTabularFromText(text: string): { headers: string[]; rows: string[][] } {
  const trimmed = text.trim();
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      const sheet = parseJsonToSheet(parsed);
      if (sheet !== null) return sheet;
    } catch {
      /* fall through to CSV */
    }
  }
  return parseCsv(text);
}

export function parseSelectOptions(value: any): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((o) => String(o).trim()).filter(Boolean);
  if (typeof value === "string") return value.split(",").map((o) => o.trim()).filter(Boolean);
  return [];
}

export type ValidationError = { rowIndex: number; fieldName: string; message: string };

export function validateCell(
  raw: string,
  field: { name: string; type: string; options?: any },
  rowIndex: number
): { value: any; error: ValidationError | null } {
  const empty = raw === "" || raw == null || String(raw).trim() === "";
  const str = String(raw).trim();

  if (field.name === IMPORT_ITEM_UID_FIELD) {
    return { value: empty ? null : str, error: null };
  }

  if (empty) {
    return { value: null, error: null };
  }

  switch (field.type) {
    case "number": {
      const num = Number(str);
      if (Number.isNaN(num)) {
        return {
          value: undefined,
          error: { rowIndex, fieldName: field.name, message: `"${str}" is not a valid number` },
        };
      }
      return { value: num, error: null };
    }
    case "checkbox": {
      const lower = str.toLowerCase();
      const truthy = ["1", "true", "yes", "on"].includes(lower);
      const falsy = ["0", "false", "no", "off"].includes(lower);
      if (!truthy && !falsy) {
        return {
          value: undefined,
          error: { rowIndex, fieldName: field.name, message: `"${str}" is not a valid checkbox (use 1/0, yes/no, true/false)` },
        };
      }
      return { value: truthy, error: null };
    }
    case "timeonly": {
      const d = parseImportTemporal(str, { timeOnly: true });
      if (!d) {
        return {
          value: undefined,
          error: {
            rowIndex,
            fieldName: field.name,
            message: `"${str}" is not a valid time (use 15:20 or 15:20:00)`,
          },
        };
      }
      return { value: d.toISOString(), error: null };
    }
    case "datetime":
    case "dateonly":
    case "calendar": {
      const d = parseImportTemporal(str);
      if (!d) {
        return {
          value: undefined,
          error: { rowIndex, fieldName: field.name, message: `"${str}" is not a valid date/time` },
        };
      }
      return { value: d.toISOString(), error: null };
    }
    case "select":
    case "dropdown": {
      const options = parseSelectOptions(field.options);
      if (options.length > 0) {
        const match = options.find((o) => o.toLowerCase() === str.toLowerCase());
        if (!match) {
          return {
            value: undefined,
            error: {
              rowIndex,
              fieldName: field.name,
              message: `"${str}" must be one of: ${options.slice(0, 5).join(", ")}${options.length > 5 ? "..." : ""}`,
            },
          };
        }
        return { value: match, error: null };
      }
      return { value: str, error: null };
    }
    case "relation":
    case "multiplerelation":
    case "systemrelation":
      return { value: str, error: null };
    case "text":
    case "textarea":
    case "richtext":
    case "code":
    case "hidden":
    default:
      return { value: str, error: null };
  }
}

export type TableField = {
  name: string;
  label?: string;
  type: string;
  options?: any;
  /** Related collection slug (relation / multiplerelation / systemrelation). */
  value?: string;
  fields?: unknown;
  sourceField?: string;
};

function isRelationFieldType(field: Pick<TableField, "type">): boolean {
  return (
    field.type === "relation" ||
    field.type === "multiplerelation" ||
    field.type === "systemrelation"
  );
}

/** Picks one CSV column per field (no duplicate columns). Values are Select-compatible (header or index prefix). */
export function suggestColumnMapping(
  importableFields: TableField[],
  headers: string[]
): Record<string, string> {
  const mapping: Record<string, string> = {};
  const usedIndices = new Set<number>();

  const pickIndex = (predicate: (header: string, index: number) => boolean): number => {
    for (let i = 0; i < headers.length; i++) {
      if (usedIndices.has(i)) continue;
      if (predicate(headers[i], i)) return i;
    }
    return -1;
  };

  for (const field of importableFields) {
    if (field.name === IMPORT_ITEM_UID_FIELD) {
      let idx = pickIndex((h) => h.toLowerCase().trim() === "uid");
      if (idx < 0) {
        idx = pickIndex((h) => normalizeHeaderToken(h) === "uid");
      }
      if (idx >= 0) {
        usedIndices.add(idx);
        mapping[field.name] = columnSelectValue(idx, headers[idx]);
      }
      continue;
    }
    const nameLower = field.name.toLowerCase();
    const labelLower = field.label?.toLowerCase();
    const nameNorm = normalizeHeaderToken(field.name);
    const labelNorm = field.label ? normalizeHeaderToken(field.label) : "";

    let idx = pickIndex((h) => h.toLowerCase() === nameLower);
    if (idx < 0 && labelLower !== undefined) {
      idx = pickIndex((h) => h.toLowerCase() === labelLower);
    }
    if (idx < 0 && nameNorm.length > 0) {
      idx = pickIndex((h) => normalizeHeaderToken(h) === nameNorm);
    }
    if (idx < 0 && labelNorm.length > 0) {
      idx = pickIndex((h) => normalizeHeaderToken(h) === labelNorm);
    }

    if (idx >= 0) {
      usedIndices.add(idx);
      mapping[field.name] = columnSelectValue(idx, headers[idx]);
    }
  }

  return mapping;
}

type CsvImportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  fields: TableField[];
  onImported: () => void;
  /** Organization segment for org-scoped collections (query param on create/update). */
  organization?: string | null;
};

function stripImportPayload(record: Record<string, unknown>): Record<string, unknown> {
  const out = { ...record };
  delete out.uid;
  delete (out as Record<string, unknown>)[IMPORT_ITEM_UID_FIELD];
  return out;
}

export function CsvImportDialog({
  open,
  onOpenChange,
  slug,
  fields,
  onImported,
  organization,
}: CsvImportDialogProps) {
  const [csvText, setCsvText] = useState("");
  const [sheetParsed, setSheetParsed] = useState<{ headers: string[]; rows: string[][] } | null>(
    null
  );
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);
  const [importPhase, setImportPhase] = useState<"mapping" | "relations">("mapping");
  const [relationLookups, setRelationLookups] = useState<Record<string, RelationLookup>>({});
  const [relationItemsByField, setRelationItemsByField] = useState<
    Record<string, Record<string, unknown>[]>
  >({});
  const [relationUidOverrides, setRelationUidOverrides] = useState<Record<string, string>>({});
  const [relationFixTarget, setRelationFixTarget] = useState<{
    rowIndex: number;
    fieldName: string;
    message: string;
  } | null>(null);
  const [relationFetchLoading, setRelationFetchLoading] = useState(false);
  const [relationLoadError, setRelationLoadError] = useState<string | null>(null);

  const { headers, rows } = useMemo(() => {
    if (sheetParsed) return sheetParsed;
    return parseTabularFromText(csvText);
  }, [csvText, sheetParsed]);

  const setMapping = useCallback((fieldName: string, csvCol: string) => {
    setColumnMapping((prev) => (csvCol === EMPTY ? (() => { const next = { ...prev }; delete next[fieldName]; return next; })() : { ...prev, [fieldName]: csvCol }));
  }, []);

  const hasSchemaUidField = useMemo(
    () => fields.some((f) => f.name === "uid"),
    [fields]
  );

  const importableFields = useMemo(
    () => fields.filter((f) => f.type !== "image" && f.type !== "file"),
    [fields]
  );

  const importableFieldsForMapping = useMemo(() => {
    if (hasSchemaUidField) return importableFields;
    return [
      { name: IMPORT_ITEM_UID_FIELD, label: "Item uid (optional, for updates)", type: "text" },
      ...importableFields,
    ];
  }, [hasSchemaUidField, importableFields]);

  const getItemUidForRow = useCallback(
    (rowIndex: number, record: Record<string, unknown>): string | null => {
      if (!hasSchemaUidField) {
        const syn = columnMapping[IMPORT_ITEM_UID_FIELD];
        if (syn !== undefined && syn !== EMPTY) {
          const colIndex = columnIndexFromMappedValue(syn, headers);
          const row = rows[rowIndex];
          if (colIndex >= 0 && row && colIndex < row.length) {
            const raw = String(row[colIndex] ?? "").trim();
            if (raw) return raw;
          }
        }
      } else if (record.uid != null) {
        const u = String(record.uid).trim();
        if (u) return u;
      }
      return null;
    },
    [hasSchemaUidField, columnMapping, headers, rows]
  );

  const validated = useMemo(() => {
    const errors: ValidationError[] = [];
    const data: Record<string, any>[] = [];
    const fieldsToImport = importableFieldsForMapping;
    for (let r = 0; r < rows.length; r++) {
      const row = rows[r];
      const record: Record<string, any> = {};
      for (const field of fieldsToImport) {
        const csvCol = columnMapping[field.name];
        const colIndex =
          csvCol !== undefined ? columnIndexFromMappedValue(csvCol, headers) : -1;
        const raw =
          colIndex >= 0 && colIndex < row.length ? row[colIndex] ?? "" : "";
        const { value, error } = validateCell(raw, field, r + 2);
        if (error) errors.push(error);
        if (value !== undefined) record[field.name] = value;
      }
      data.push(record);
    }
    return { errors, data };
  }, [rows, headers, columnMapping, importableFieldsForMapping]);

  const hasMapping = Object.keys(columnMapping).length > 0;
  const canImport = validated.errors.length === 0 && validated.data.length > 0 && hasMapping;
  const previewData = validated.data.slice(0, PREVIEW_ROWS);

  const mappedRelationFields = useMemo(
    () =>
      importableFields.filter(
        (f) => isRelationFieldType(f) && columnMapping[f.name] !== undefined && columnMapping[f.name] !== EMPTY
      ),
    [importableFields, columnMapping]
  );

  const resolvedWithRelations = useMemo(() => {
    const errors: ValidationError[] = [];
    const data: Record<string, unknown>[] = [];
    if (importPhase !== "relations" || relationFetchLoading || Object.keys(relationLookups).length === 0) {
      return { errors, data };
    }
    const fieldsToImport = importableFieldsForMapping;
    for (let r = 0; r < rows.length; r++) {
      const row = rows[r];
      const record: Record<string, unknown> = {};
      for (const field of fieldsToImport) {
        if (field.name === IMPORT_ITEM_UID_FIELD) continue;
        const csvCol = columnMapping[field.name];
        const colIndex =
          csvCol !== undefined ? columnIndexFromMappedValue(csvCol, headers) : -1;
        const raw =
          colIndex >= 0 && colIndex < row.length ? row[colIndex] ?? "" : "";
        const isRel = isRelationFieldType(field) && csvCol !== undefined;
        if (isRel) {
          const overrideKey = `${r}-${field.name}`;
          const overrideUid = relationUidOverrides[overrideKey];
          if (overrideUid) {
            if (field.type === "multiplerelation") {
              record[field.name] = overrideUid.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
            } else {
              record[field.name] = overrideUid;
            }
            continue;
          }
          const lookup = relationLookups[field.name];
          if (!lookup) {
            errors.push({
              rowIndex: r + 2,
              fieldName: field.name,
              message: "Related collection data is not loaded",
            });
            continue;
          }
          if (field.type === "multiplerelation") {
            const { uids, error } = resolveMultiRelationValueForImport(String(raw), lookup);
            if (error) errors.push({ rowIndex: r + 2, fieldName: field.name, message: error });
            else record[field.name] = uids ?? [];
          } else {
            const { uid, error } = resolveRelationValueForImport(String(raw), lookup);
            if (error) errors.push({ rowIndex: r + 2, fieldName: field.name, message: error });
            else if (uid) record[field.name] = uid;
            else record[field.name] = null;
          }
        } else {
          const { value, error } = validateCell(String(raw), field, r + 2);
          if (error) errors.push(error);
          if (value !== undefined) record[field.name] = value;
        }
      }
      data.push(record);
    }
    return { errors, data };
  }, [
    importPhase,
    relationFetchLoading,
    relationLookups,
    rows,
    headers,
    columnMapping,
    importableFieldsForMapping,
    relationUidOverrides,
  ]);

  const relationUniqueSummary = useMemo(() => {
    if (importPhase !== "relations" || relationFetchLoading || mappedRelationFields.length === 0) {
      return [] as {
        fieldLabel: string;
        fieldName: string;
        raw: string;
        ok: boolean;
        resolved: string;
      }[];
    }
    const lines: { fieldLabel: string; fieldName: string; raw: string; ok: boolean; resolved: string }[] = [];
    for (const f of mappedRelationFields) {
      const lookup = relationLookups[f.name];
      const seen = new Set<string>();
      for (let r = 0; r < rows.length; r++) {
        const row = rows[r];
        const csvCol = columnMapping[f.name];
        const colIndex =
          csvCol !== undefined ? columnIndexFromMappedValue(csvCol, headers) : -1;
        const raw =
          colIndex >= 0 && colIndex < row.length ? String(row[colIndex] ?? "").trim() : "";
        if (!raw) continue;
        const overrideKey = `${r}-${f.name}`;
        const overrideUid = relationUidOverrides[overrideKey];
        if (overrideUid) {
          const sig = `${f.name}\u0000${raw}`;
          if (seen.has(sig)) continue;
          seen.add(sig);
          lines.push({
            fieldLabel: f.label || f.name,
            fieldName: f.name,
            raw,
            ok: true,
            resolved: overrideUid,
          });
          continue;
        }
        const sig = `${f.name}\u0000${raw}`;
        if (seen.has(sig)) continue;
        seen.add(sig);
        if (!lookup) {
          lines.push({
            fieldLabel: f.label || f.name,
            fieldName: f.name,
            raw,
            ok: false,
            resolved: "—",
          });
          continue;
        }
        if (f.type === "multiplerelation") {
          const { uids, error } = resolveMultiRelationValueForImport(raw, lookup);
          lines.push({
            fieldLabel: f.label || f.name,
            fieldName: f.name,
            raw,
            ok: !error,
            resolved: error ? error : (uids?.join(", ") ?? "—"),
          });
        } else {
          const { uid, error } = resolveRelationValueForImport(raw, lookup);
          lines.push({
            fieldLabel: f.label || f.name,
            fieldName: f.name,
            raw,
            ok: !error,
            resolved: error ? error : (uid ?? "—"),
          });
        }
      }
    }
    return lines;
  }, [
    importPhase,
    relationFetchLoading,
    mappedRelationFields,
    relationLookups,
    rows,
    headers,
    columnMapping,
    relationUidOverrides,
  ]);

  const canImportAfterRelationReview =
    importPhase === "relations" &&
    !relationFetchLoading &&
    !relationLoadError &&
    resolvedWithRelations.errors.length === 0 &&
    resolvedWithRelations.data.length > 0;

  /** Rows that include an item uid in the file (PUT first; 404 → create). Not "existing rows" in this collection. */
  const fileUidRowCounts = useMemo(() => {
    let withUidInFile = 0;
    let withoutUid = 0;
    for (let i = 0; i < validated.data.length; i++) {
      if (getItemUidForRow(i, validated.data[i])) withUidInFile += 1;
      else withoutUid += 1;
    }
    return { withUidInFile, withoutUid };
  }, [validated.data, getItemUidForRow]);

  const relationsFileUidRowCounts = useMemo(() => {
    let withUidInFile = 0;
    let withoutUid = 0;
    for (let i = 0; i < resolvedWithRelations.data.length; i++) {
      if (getItemUidForRow(i, resolvedWithRelations.data[i])) withUidInFile += 1;
      else withoutUid += 1;
    }
    return { withUidInFile, withoutUid };
  }, [resolvedWithRelations.data, getItemUidForRow]);

  const relationFixPreviewObject = useMemo(() => {
    if (!relationFixTarget) return {};
    const rowIdx = relationFixTarget.rowIndex;
    const vr = validated.data[rowIdx];
    if (!vr) return {};
    const rr = resolvedWithRelations.data[rowIdx];
    const out: Record<string, unknown> = {};
    const itemUid = getItemUidForRow(rowIdx, vr);
    if (itemUid) out.uid = itemUid;
    for (const f of importableFields) {
      if (f.name === IMPORT_ITEM_UID_FIELD) continue;
      if (isRelationFieldType(f)) {
        if (f.name === relationFixTarget.fieldName) {
          out[f.name] = vr[f.name];
        } else {
          out[f.name] = rr?.[f.name] ?? vr[f.name];
        }
      } else {
        out[f.name] = vr[f.name];
      }
    }
    return out;
  }, [relationFixTarget, validated.data, resolvedWithRelations.data, importableFields, getItemUidForRow]);

  const relationFixField = useMemo(() => {
    if (!relationFixTarget) return null;
    return mappedRelationFields.find((f) => f.name === relationFixTarget.fieldName) ?? null;
  }, [relationFixTarget, mappedRelationFields]);

  const relationFixSameValueRowCount = useMemo(() => {
    if (!relationFixTarget) return 0;
    const { rowIndex, fieldName } = relationFixTarget;
    const norm = rawCsvCellForMappedField(
      rowIndex,
      fieldName,
      columnMapping,
      headers,
      rows
    ).trim();
    let count = 0;
    for (let r = 0; r < rows.length; r++) {
      if (
        rawCsvCellForMappedField(r, fieldName, columnMapping, headers, rows).trim() === norm
      ) {
        count++;
      }
    }
    return count;
  }, [relationFixTarget, columnMapping, headers, rows]);

  const handleDialogOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        setImportPhase("mapping");
        setRelationLookups({});
        setRelationItemsByField({});
        setRelationUidOverrides({});
        setRelationFixTarget(null);
        setRelationLoadError(null);
      }
      onOpenChange(next);
    },
    [onOpenChange]
  );

  const handleImport = useCallback(async () => {
    if (mappedRelationFields.length > 0) {
      if (!canImportAfterRelationReview) return;
      setImporting(true);
      try {
        for (let i = 0; i < resolvedWithRelations.data.length; i++) {
          const record = resolvedWithRelations.data[i];
          const itemUid = getItemUidForRow(i, record);
          const payload = stripImportPayload(record as Record<string, unknown>);
          await importRowCreateOrUpdate(slug, itemUid, payload, organization);
        }
        onImported();
        handleDialogOpenChange(false);
        setCsvText("");
        setSheetParsed(null);
        setColumnMapping({});
        setImportPhase("mapping");
        setRelationLookups({});
        setRelationLoadError(null);
      } catch (err) {
        console.error("CSV import failed:", err);
        alert(err instanceof Error ? err.message : "Import failed");
      } finally {
        setImporting(false);
      }
      return;
    }
    if (!canImport) return;
    setImporting(true);
    try {
      for (let i = 0; i < validated.data.length; i++) {
        const record = validated.data[i];
        const itemUid = getItemUidForRow(i, record);
        const payload = stripImportPayload(record as Record<string, unknown>);
        await importRowCreateOrUpdate(slug, itemUid, payload, organization);
      }
      onImported();
      handleDialogOpenChange(false);
      setCsvText("");
      setSheetParsed(null);
      setColumnMapping({});
      setImportPhase("mapping");
      setRelationLookups({});
      setRelationLoadError(null);
    } catch (err) {
      console.error("CSV import failed:", err);
      alert(err instanceof Error ? err.message : "Import failed");
    } finally {
      setImporting(false);
    }
  }, [
    mappedRelationFields.length,
    canImportAfterRelationReview,
    resolvedWithRelations.data,
    canImport,
    validated.data,
    slug,
    onImported,
    handleDialogOpenChange,
    organization,
    getItemUidForRow,
  ]);

  const goToRelationReview = useCallback(() => {
    if (!canImport || mappedRelationFields.length === 0) return;
    setRelationUidOverrides({});
    setRelationFixTarget(null);
    setImportPhase("relations");
    setRelationLookups({});
    setRelationItemsByField({});
    setRelationLoadError(null);
  }, [canImport, mappedRelationFields.length]);

  const headersKey = useMemo(() => headers.join("\u0001"), [headers]);

  useEffect(() => {
    if (!open) {
      setImportPhase("mapping");
      setRelationLookups({});
      setRelationItemsByField({});
      setRelationUidOverrides({});
      setRelationFixTarget(null);
      setRelationLoadError(null);
      setRelationFetchLoading(false);
    }
  }, [open]);

  useEffect(() => {
    setImportPhase("mapping");
  }, [csvText, sheetParsed, headersKey]);

  useEffect(() => {
    if (!open || importPhase !== "relations" || mappedRelationFields.length === 0) return;
    let cancelled = false;
    setRelationFetchLoading(true);
    setRelationLoadError(null);
    void (async () => {
      try {
        const lookups: Record<string, RelationLookup> = {};
        const itemsByField: Record<string, Record<string, unknown>[]> = {};
        for (const f of mappedRelationFields) {
          const key = typeof f.value === "string" ? f.value : "";
          if (!key) {
            throw new Error(`Field "${f.label || f.name}" has no related collection configured.`);
          }
          const items = await fetchAllRelatedItems(key, f.type);
          itemsByField[f.name] = items;
          lookups[f.name] = buildRelationLookup(items, f);
        }
        if (!cancelled) {
          setRelationLookups(lookups);
          setRelationItemsByField(itemsByField);
        }
      } catch (e) {
        if (!cancelled) {
          setRelationLoadError(e instanceof Error ? e.message : "Failed to load related collections");
        }
      } finally {
        if (!cancelled) setRelationFetchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, importPhase, mappedRelationFields]);

  useEffect(() => {
    setColumnMapping(suggestColumnMapping(importableFieldsForMapping, headers));
  }, [headersKey, importableFieldsForMapping]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const onImportFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const lower = file.name.toLowerCase();
    const isXlsx =
      lower.endsWith(".xlsx") ||
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    if (isXlsx) {
      void (async () => {
        try {
          const buf = await file.arrayBuffer();
          const parsed = await parseXlsxFirstSheet(buf);
          setSheetParsed(parsed);
          setCsvText("");
        } catch (err) {
          console.error("Excel import failed:", err);
          alert(err instanceof Error ? err.message : "Could not read Excel file");
        }
      })();
      return;
    }
    const isJson =
      lower.endsWith(".json") ||
      file.type === "application/json" ||
      file.type === "text/json";
    if (isJson) {
      void (async () => {
        try {
          const text = await file.text();
          const trimmed = text.trim();
          const parsed = JSON.parse(trimmed) as unknown;
          const sheet = parseJsonToSheet(parsed);
          if (!sheet) {
            alert(
              'Invalid JSON for import. Use a top-level array of objects, or an array of arrays with a header row.'
            );
            return;
          }
          setSheetParsed(sheet);
          setCsvText("");
        } catch (err) {
          console.error("JSON import failed:", err);
          alert(err instanceof Error ? err.message : "Could not read JSON file");
        }
      })();
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      setSheetParsed(null);
      setCsvText(text);
    };
    reader.readAsText(file, "UTF-8");
  }, []);

  const applyAutoMatch = useCallback(() => {
    setColumnMapping(suggestColumnMapping(importableFieldsForMapping, headers));
  }, [importableFieldsForMapping, headers]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import from CSV, JSON, or Excel</DialogTitle>
            <DialogDescription className="space-y-1.5">
              <span className="block">
                Paste CSV or JSON, or upload .csv / .json / .xlsx. For CSV and Excel, the first row should be
                headers (first sheet for Excel). For JSON, use a top-level array of objects, or an array of
                arrays whose first row is the header.
              </span>
              <span className="block">Columns are auto-matched to fields by name or label when the header row changes; adjust mappings as needed.</span>
              <span className="block">
                All rows are validated before import. If you map relation columns, the next step loads related
                collections and shows how each imported value resolves to a uid (by uid, code, or label) before any rows
                are created. Rows with a non-empty item uid (column <code className="text-xs">uid</code> or field{" "}
                <code className="text-xs">uid</code>) are sent as an update first; if that item does not exist in this
                collection, a new row is created instead, using the same uid from the file when the server accepts it.
                Preview counts only reflect whether the file includes a uid, not whether the item already exists here.
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {importPhase === "relations" ? (
              <div className="space-y-3">
                <p className="text-sm font-medium">Confirm relation values</p>
                <p className="text-xs text-muted-foreground">
                  Imported text in relation columns is resolved to item uids in the related collection. Multiple values
                  for a multi-relation may be separated by commas or semicolons. Fix any errors in the file or mappings,
                  then use Back to edit.
                </p>
                {relationFetchLoading && (
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                    Loading related collections…
                  </p>
                )}
                {relationLoadError && (
                  <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                    {relationLoadError}
                  </div>
                )}
                {!relationFetchLoading && !relationLoadError && relationUniqueSummary.length > 0 && (
                  <div className="rounded-md border overflow-x-auto text-xs max-h-56 overflow-y-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-muted/50">
                          <th className="border p-1.5 text-left font-medium">Field</th>
                          <th className="border p-1.5 text-left font-medium">Imported value</th>
                          <th className="border p-1.5 text-left font-medium">Resolved uid(s)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {relationUniqueSummary.map((line, i) => (
                          <tr key={i} className="bg-background">
                            <td className="border p-1.5">{line.fieldLabel}</td>
                            <td className="border p-1.5 font-mono max-w-[180px] truncate" title={line.raw}>
                              {line.raw}
                            </td>
                            <td
                              className={`border p-1.5 font-mono max-w-[220px] truncate ${line.ok ? "" : "text-destructive"}`}
                              title={line.resolved}
                            >
                              {line.resolved}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {resolvedWithRelations.errors.length > 0 && (
                  <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 space-y-1">
                    <div className="flex items-center gap-2 text-destructive font-medium text-sm">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      Relation resolution errors
                    </div>
                    <ul className="text-sm text-destructive/90 list-none space-y-2 max-h-48 overflow-y-auto pl-0">
                      {resolvedWithRelations.errors.slice(0, 25).map((e, i) => {
                        const fixField = mappedRelationFields.find((f) => f.name === e.fieldName);
                        const showFix =
                          isRelationNoMatchError(e.message) &&
                          fixField &&
                          fixField.type !== "multiplerelation";
                        return (
                          <li key={i} className="flex flex-wrap items-start gap-x-2 gap-y-1 border-b border-destructive/20 pb-2 last:border-0 last:pb-0">
                            <span className="min-w-0 flex-1">
                              Row {e.rowIndex} ({e.fieldName}): {e.message}
                            </span>
                            {showFix ? (
                              <button
                                type="button"
                                className="shrink-0 text-sm font-medium text-primary underline underline-offset-2 hover:no-underline"
                                onClick={() =>
                                  setRelationFixTarget({
                                    rowIndex: e.rowIndex - 2,
                                    fieldName: e.fieldName,
                                    message: e.message,
                                  })
                                }
                              >
                                Fix
                              </button>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {canImportAfterRelationReview && (
                  <div className="rounded-md border border-green-500/30 bg-green-500/10 p-3 space-y-1 text-green-700 dark:text-green-400 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 shrink-0" />
                      All relation values resolved. You can import {resolvedWithRelations.data.length} row(s).
                    </div>
                    <p className="text-xs pl-6 text-green-800/90 dark:text-green-300/90">
                      Uid in file (PUT first; create if not found):{" "}
                      <span className="font-medium tabular-nums">{relationsFileUidRowCounts.withUidInFile}</span>
                      {" · "}
                      No uid in file (create only):{" "}
                      <span className="font-medium tabular-nums">{relationsFileUidRowCounts.withoutUid}</span>
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <Label htmlFor="csv-paste">
                      {sheetParsed ? "Paste to replace (CSV or JSON)" : "CSV or JSON data"}
                    </Label>
                    <div className="flex items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.json,.xlsx,.txt,text/csv,text/plain,text/json,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        className="sr-only"
                        aria-label="Upload CSV, JSON, Excel, or text file"
                        onChange={onImportFileChange}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Upload file
                      </Button>
                    </div>
                  </div>
                  <textarea
                    id="csv-paste"
                    className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                    placeholder={'CSV example:\nName,Quantity\nWidget A,10\n\nJSON example:\n[{"name":"Widget A","quantity":10}]'}
                    value={csvText}
                    onChange={(e) => {
                      setSheetParsed(null);
                      setCsvText(e.target.value);
                    }}
                  />
                  {sheetParsed && (
                    <p className="text-xs text-muted-foreground">
                      Data is loaded from the file. Paste CSV or JSON, or choose another file to replace it.
                    </p>
                  )}
                </div>

                {headers.length > 0 && (
                  <>
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Label className="mb-0">Map columns to table fields</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 shrink-0"
                          onClick={applyAutoMatch}
                        >
                          Auto-match columns
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        File columns: {headers.join(", ")}
                      </p>
                      <div className="grid gap-2 max-h-48 overflow-y-auto">
                        {importableFieldsForMapping.map((field) => (
                          <div key={field.name} className="flex items-center gap-2">
                            <span className="text-sm w-32 shrink-0 truncate" title={field.name}>
                              {field.label || field.name}
                            </span>
                            <Select
                              value={columnMapping[field.name] ?? EMPTY}
                              onValueChange={(v) => setMapping(field.name, v)}
                            >
                              <SelectTrigger className="flex-1">
                                <SelectValue placeholder="Skip" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={EMPTY}>Skip</SelectItem>
                                {headers.map((h, i) => (
                                  <SelectItem key={`${i}-${columnSelectValue(i, h)}`} value={columnSelectValue(i, h)}>
                                    {h || "(empty column)"}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>

                    {validated.errors.length > 0 && (
                      <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 space-y-1">
                        <div className="flex items-center gap-2 text-destructive font-medium text-sm">
                          <AlertCircle className="h-4 w-4 shrink-0" />
                          Validation errors (fix these before importing)
                        </div>
                        <ul className="text-sm text-destructive/90 list-disc list-inside max-h-32 overflow-y-auto">
                          {validated.errors.slice(0, 20).map((e, i) => (
                            <li key={i}>
                              Row {e.rowIndex} ({e.fieldName}): {e.message}
                            </li>
                          ))}
                          {validated.errors.length > 20 && (
                            <li>... and {validated.errors.length - 20} more</li>
                          )}
                        </ul>
                      </div>
                    )}

                    {validated.data.length > 0 && validated.errors.length === 0 && (
                      <div className="rounded-md border border-green-500/30 bg-green-500/10 p-3 space-y-1 text-green-700 dark:text-green-400 text-sm">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 shrink-0" />
                          All {validated.data.length} row(s) passed validation.
                        </div>
                        <p className="text-xs pl-6 text-green-800/90 dark:text-green-300/90">
                          Uid in file (PUT first; create if not found):{" "}
                          <span className="font-medium tabular-nums">{fileUidRowCounts.withUidInFile}</span>
                          {" · "}
                          No uid in file (create only):{" "}
                          <span className="font-medium tabular-nums">{fileUidRowCounts.withoutUid}</span>
                        </p>
                      </div>
                    )}

                    <div className="space-y-1">
                      <div>
                        <Label>Example (first {PREVIEW_ROWS} rows)</Label>
                        {validated.data.length > 0 && validated.errors.length === 0 && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Same totals: {fileUidRowCounts.withUidInFile} row(s) include a uid in the file (not proof the item
                            exists here); {fileUidRowCounts.withoutUid} row(s) have no uid (always create).
                          </p>
                        )}
                      </div>
                      <div className="rounded-md border overflow-x-auto text-xs">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-muted/50">
                              <th className="border p-1 text-left font-medium">#</th>
                              <th className="border p-1 text-left font-medium whitespace-nowrap" title="Whether the row has an item uid in the file (PUT first). Does not mean the item already exists in this collection.">
                                Uid in file
                              </th>
                              {importableFieldsForMapping.map((f) => (
                                <th key={f.name} className="border p-1 text-left font-medium truncate max-w-[120px]" title={f.name}>
                                  {f.label || f.name}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.map((row, idx) => (
                              <tr key={idx} className="bg-background">
                                <td className="border p-1 text-muted-foreground">{idx + 1}</td>
                                <td className="border p-1 whitespace-nowrap text-muted-foreground">
                                  {getItemUidForRow(idx, row) ? "Yes" : "No"}
                                </td>
                                {importableFieldsForMapping.map((f) => (
                                  <td key={f.name} className="border p-1 truncate max-w-[120px]" title={String(row[f.name] ?? "")}>
                                    {row[f.name] != null ? String(row[f.name]) : ""}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {validated.data.length > PREVIEW_ROWS && (
                        <p className="text-xs text-muted-foreground">
                          ... and {validated.data.length - PREVIEW_ROWS} more row(s)
                        </p>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <DialogFooter className="sm:justify-start">
            <Button variant="outline" onClick={() => handleDialogOpenChange(false)} disabled={importing}>
              Cancel
            </Button>
            {importPhase === "mapping" ? (
              mappedRelationFields.length > 0 ? (
                <Button
                  variant="primary"
                  onClick={goToRelationReview}
                  disabled={!canImport || importing}
                >
                  Review relation fields
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleImport}
                  disabled={!canImport || importing}
                >
                  {importing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    `Import ${validated.data.length} row${validated.data.length === 1 ? "" : "s"} (uid in file: ${fileUidRowCounts.withUidInFile}, no uid: ${fileUidRowCounts.withoutUid})`
                  )}
                </Button>
              )
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setRelationUidOverrides({});
                    setRelationFixTarget(null);
                    setImportPhase("mapping");
                  }}
                  disabled={importing}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={handleImport}
                  disabled={!canImportAfterRelationReview || importing}
                >
                  {importing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    `Import ${resolvedWithRelations.data.length} row${resolvedWithRelations.data.length === 1 ? "" : "s"} (uid in file: ${relationsFileUidRowCounts.withUidInFile}, no uid: ${relationsFileUidRowCounts.withoutUid})`
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CsvImportRelationFixDialog
        open={relationFixTarget !== null}
        onOpenChange={(next) => {
          if (!next) setRelationFixTarget(null);
        }}
        field={relationFixField}
        relatedItems={
          relationFixTarget ? relationItemsByField[relationFixTarget.fieldName] ?? [] : []
        }
        previewObject={relationFixPreviewObject}
        highlightFieldName={relationFixTarget?.fieldName ?? ""}
        resolutionErrorText={relationFixTarget?.message ?? ""}
        sameValueRowCount={relationFixSameValueRowCount}
        onApply={(uid, scope) => {
          if (!relationFixTarget) return;
          const { rowIndex, fieldName } = relationFixTarget;
          if (scope === "row") {
            setRelationUidOverrides((prev) => ({
              ...prev,
              [`${rowIndex}-${fieldName}`]: uid,
            }));
          } else {
            const norm = rawCsvCellForMappedField(
              rowIndex,
              fieldName,
              columnMapping,
              headers,
              rows
            ).trim();
            setRelationUidOverrides((prev) => {
              const next = { ...prev };
              for (let r = 0; r < rows.length; r++) {
                if (
                  rawCsvCellForMappedField(r, fieldName, columnMapping, headers, rows).trim() ===
                  norm
                ) {
                  next[`${r}-${fieldName}`] = uid;
                }
              }
              return next;
            });
          }
          setRelationFixTarget(null);
        }}
      />
    </>
  );
}
